import { Key, RefObject, useCallback, useState } from 'react';
import { Node, Selection } from '@react-types/shared';

import { Item, ListItem, SelectionMode, useListState } from '@xemplo/listbox';
import { usePopoverState } from '@xemplo/popover';

import { selectionHandler } from '../v2/text-dropdown-multi.helper';
import { BaseTextDropdownMultiProps } from '../v2/text-dropdown-multi-v2.types';

import { useSelectProps } from './use-select-props';

type UseMultiStatePropsV2<T extends ListItem> = {
  originalProps: BaseTextDropdownMultiProps<T>;
  containerRef: RefObject<HTMLElement>;
  selectRef: RefObject<HTMLSelectElement>;
  listRef: RefObject<HTMLUListElement>;
  singleValueRef?: RefObject<HTMLDivElement> | null;
};

export const useMultiStateV2 = <T extends ListItem>(props: UseMultiStatePropsV2<T>) => {
  const { originalProps, containerRef, selectRef, singleValueRef } = props;
  const {
    disabledKeys,
    items,
    defaultSelectedKeys,
    disallowEmptySelection = false,
    isDropdownMulti,
  } = originalProps;

  const [searchValue, setSearchValue] = useState('');
  const { state: popoverState } = usePopoverState({ ref: containerRef, type: 'listbox' });

  /**
   * This is triggered when the selectionManager.selectedKeys from listState changes
   * Previously, it was manipulating the HTML Select element directly and this
   * was causing issues with the controlled state of the component.
   *
   * The onSelectionChange will be responsible for synchronizing the selected keys
   * with the react-hook-form state if that is being used. Additionally, it will
   * trigger the onChange event that can be used by the parent implementation.
   * Previously it was providing the HTMLSelect element as the value, but since
   * we are no longer updating the HTMLSelect element directly, we need to provide the
   * selected keys instead. Which in turn, is a lot cleaner and easier to understand.
   */
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      const { isDropdownMulti, items, onSelectionChange, onChange } = originalProps;
      const value = selectionHandler<T>(keys, items, isDropdownMulti);
      onSelectionChange?.(value);

      // Typescript got really dumb here. I made conditional type to allow
      // better usage of the onChange event. Otherwise, during implementation
      // we would be forced to check for array or not. But it doesn't quite
      // understand that the isDropdownMulti prop is the one that determines
      // the type of the value. So we need to cast it to the correct type.
      if (isDropdownMulti) {
        onChange?.(value as Key[]);
      } else {
        popoverState.close();
        onChange?.(value as Key);
      }
    },
    [originalProps, popoverState]
  );

  const listState = useListState<T>({
    selectionMode: isDropdownMulti ? SelectionMode.Multiple : SelectionMode.Single,
    disabledKeys,
    disallowEmptySelection,
    defaultSelectedKeys,
    children: items
      ?.filter((item) => item.key !== '-1' && item.key !== -1)
      .map((item) => <Item key={item.key}>{item.value}</Item>),
    filter: (nodes) => filterFn(nodes, searchValue),
    onSelectionChange: handleSelectionChange,
  });

  const searchState = { searchValue, setSearchValue };
  const selectPropsInternal = useSelectProps({
    ...props,
    listState,
    popoverState,
  });

  const handleContainerClick = useCallback(() => {
    const { isOpen, open, close } = popoverState;
    isOpen ? close() : open();

    const ref = isDropdownMulti ? selectRef.current : singleValueRef?.current;
    ref?.[isOpen ? 'blur' : 'focus']();
  }, [popoverState, selectRef, singleValueRef, isDropdownMulti]);

  return {
    listState,
    popoverState,
    searchState,
    selectPropsInternal,
    handleContainerClick,
  };
};

function filterFn<T>(nodes: Iterable<Node<T>>, search: string) {
  return Array.from(nodes).filter((node) => {
    const text = node.textValue?.toLowerCase();
    return text?.includes(search.toLowerCase());
  }) as Iterable<Node<T>>;
}
